/**
 * `/settings/logs/:auditId` — single audit detail.
 *
 * Replacement for legacy `AuditDetailIsland.tsx`. Polls while the
 * audit is pending/running (wired by Stage 4's `useAudit`). Tabs:
 *
 *   - Overview     — headline scores + Core Web Vitals cards.
 *   - Diagnostics  — raw error metadata + IDs (placeholder — the
 *                    per-audit Lighthouse diagnostics body lives in
 *                    the R2 report, not the API row).
 *   - Raw          — the full audit JSON + a signed report link.
 *
 * The detail page is reached from three places (Home peek,
 * Origins page timeline, Settings/Logs) — topology says the
 * Settings left-pane "Logs" entry stays highlighted throughout
 * (that's handled by `SettingsNav` via prefix-matching).
 */
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import { PageActions } from '@/components/primitives/PageActions';
import { StatusBadge } from '@/components/primitives/StatusBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { AuditResponse } from '@/lib/api-types';
import { toastFromError, toastPromise, toastSuccess } from '@/lib/errors';
import {
  clsColor,
  formatMs,
  formatScore,
  inpColor,
  lcpColor,
  relativeTime,
  scoreColor,
  truncate,
} from '@/lib/format';
import { useAudit, useAuditReport, useCreateAudit } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { ArrowsClockwise, Copy, DownloadSimple, Play, Warning } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/settings/logs/$auditId')({
  component: AuditDetail,
});

function AuditDetail(): ReactElement {
  const { auditId } = Route.useParams();
  const audit = useAudit(auditId, { includeIds: true });
  const [reportEnabled, setReportEnabled] = useState(false);
  const report = useAuditReport(auditId, { enabled: reportEnabled });
  const rerun = useCreateAudit();

  async function copy(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      toastSuccess(`Copied ${label}`);
    } catch (err) {
      toastFromError(err);
    }
  }

  async function onRerun() {
    if (!audit.data) return;
    // Wrap in a loading-toast upgrade so the user gets feedback on
    // a slow on-demand audit trigger (07-polish.md §Toast polish).
    try {
      await toastPromise(rerun.mutateAsync({ body: { url: audit.data.url } }), {
        loading: 'Dispatching re-run…',
        loadingDescription: audit.data.url,
        success: 'Re-run dispatched',
      });
    } catch {
      // toastPromise already surfaced the error toast.
    }
  }

  async function onDownload() {
    setReportEnabled(true);
    // useAuditReport refetches on enable; once it resolves we open
    // the signed URL in a new tab.
    const fresh = await report.refetch();
    if (fresh.data?.url) {
      window.open(fresh.data.url, '_blank', 'noopener,noreferrer');
    }
  }

  if (audit.isLoading && !audit.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (audit.isError || !audit.data) {
    return (
      <div className="rounded-2xl border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 p-6 text-sm text-[var(--color-danger)]">
        <div className="flex items-center gap-2">
          <Warning aria-hidden="true" />
          <strong>Couldn't load this audit.</strong>
        </div>
        <p className="mt-1 text-[var(--color-ink-soft)]">
          {audit.error instanceof Error ? audit.error.message : 'Try refreshing.'}
        </p>
      </div>
    );
  }

  const a = audit.data;
  const isPending = a.status === 'pending' || a.status === 'running';
  const canDownload = a.status === 'success' || !!a.report_url;

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-[var(--color-ink)]">
              {truncate(a.url, 80)}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--color-mute)]">
              <StatusBadge status={a.status} />
              <span>{a.id}</span>
              <span>•</span>
              <span>{a.device_profile_label}</span>
              <span>•</span>
              <span>{a.region}</span>
              <span>•</span>
              <span>{relativeTime(a.created_at)}</span>
              {isPending && <Spinner className="size-3" />}
            </div>
          </div>
          <PageActions>
            <Button
              variant="outline"
              onClick={() => {
                void copy(a.id, 'audit id');
              }}
            >
              <Copy aria-hidden="true" />
              Copy id
            </Button>
            {canDownload && (
              <Button
                variant="outline"
                onClick={() => {
                  void onDownload();
                }}
                loading={report.isFetching}
              >
                <DownloadSimple aria-hidden="true" />
                Report
              </Button>
            )}
            <Button
              onClick={() => {
                void onRerun();
              }}
              loading={rerun.isPending}
            >
              <Play aria-hidden="true" />
              Re-run
            </Button>
          </PageActions>
        </div>
      </header>

      {isPending && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--color-accent-brand)]/40 bg-[var(--color-accent-brand)]/5 px-3 py-2 text-sm">
          <Spinner className="size-4 text-[var(--color-accent-brand)]" />
          <span>Polling every 3s for results. Lighthouse runs usually resolve in 15–60s.</span>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => audit.refetch()}>
            <ArrowsClockwise aria-hidden="true" />
            Poll now
          </Button>
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList variant="underline">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="diagnostics">Diagnostics</TabsTrigger>
          <TabsTrigger value="raw">Raw</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="pt-4">
          <Overview audit={a} />
        </TabsContent>
        <TabsContent value="diagnostics" className="pt-4">
          <Diagnostics audit={a} />
        </TabsContent>
        <TabsContent value="raw" className="pt-4">
          <Raw audit={a} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// -----------------------------------------------------------------
// Overview tab
// -----------------------------------------------------------------

function Overview({ audit }: { audit: AuditResponse }): ReactElement {
  if (audit.status !== 'success') {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-line)] bg-[var(--color-paper-alt)]/30 p-6 text-center text-sm text-[var(--color-mute)]">
        {audit.status === 'error' || audit.status === 'timeout'
          ? (audit.error?.message ?? 'The audit did not complete successfully.')
          : 'Scores and metrics will appear once the audit finishes.'}
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <section>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
          Scores
        </h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <ScoreCard label="Performance" value={audit.scores?.performance ?? null} />
          <ScoreCard label="Accessibility" value={audit.scores?.accessibility ?? null} />
          <ScoreCard label="Best practices" value={audit.scores?.best_practices ?? null} />
          <ScoreCard label="SEO" value={audit.scores?.seo ?? null} />
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
          Core Web Vitals
        </h3>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <CwvCard
            label="LCP"
            value={formatMs(audit.metrics?.lcp_ms ?? null)}
            tone={lcpColor(audit.metrics?.lcp_ms ?? null)}
          />
          <CwvCard
            label="CLS"
            value={audit.metrics?.cls == null ? '—' : audit.metrics.cls.toFixed(2)}
            tone={clsColor(audit.metrics?.cls ?? null)}
          />
          <CwvCard
            label="INP"
            value={formatMs(audit.metrics?.inp_ms ?? null)}
            tone={inpColor(audit.metrics?.inp_ms ?? null)}
          />
          <CwvCard label="FCP" value={formatMs(audit.metrics?.fcp_ms ?? null)} />
          <CwvCard label="TTFB" value={formatMs(audit.metrics?.ttfb_ms ?? null)} />
        </div>
      </section>
    </div>
  );
}

function ScoreCard({
  label,
  value,
}: {
  label: string;
  value: number | null;
}): ReactElement {
  return (
    <div className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
        {label}
      </div>
      <div className={cn('mt-1 text-2xl font-semibold leading-none', scoreColor(value))}>
        {formatScore(value)}
      </div>
    </div>
  );
}

function CwvCard({
  label,
  value,
  tone = 'text-[var(--color-ink)]',
}: {
  label: string;
  value: string;
  tone?: string;
}): ReactElement {
  return (
    <div className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
        {label}
      </div>
      <div className={cn('mt-1 text-xl font-semibold leading-none', tone)}>{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------
// Diagnostics tab
// -----------------------------------------------------------------

function Diagnostics({ audit }: { audit: AuditResponse }): ReactElement {
  return (
    <dl className="grid gap-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5 sm:grid-cols-2">
      <Row label="Audit id" value={audit.id} mono />
      {audit.ids && (
        <>
          <Row label="Snapshot uuid" value={audit.ids.snapshot_uuid} mono />
          <Row label="Site uuid" value={audit.ids.site_uuid} mono />
          <Row label="Page uuid" value={audit.ids.page_uuid} mono />
          <Row label="Device profile uuid" value={audit.ids.device_profile_uuid} mono />
          <Row label="Region uuid" value={audit.ids.region_uuid} mono />
        </>
      )}
      <Row label="Attempt" value={String(audit.attempt ?? 1)} />
      <Row label="Duration" value={formatMs(audit.duration_ms ?? null)} />
      <Row label="Network" value={audit.network ?? '—'} />
      <Row label="Created" value={new Date(audit.created_at).toLocaleString()} />
      {audit.started_at && (
        <Row label="Started" value={new Date(audit.started_at).toLocaleString()} />
      )}
      {audit.completed_at && (
        <Row label="Completed" value={new Date(audit.completed_at).toLocaleString()} />
      )}
      {audit.error && (
        <div className="sm:col-span-2">
          <Row label="Error code" value={audit.error.code ?? '—'} />
          <Row label="Error message" value={audit.error.message ?? '—'} />
        </div>
      )}
    </dl>
  );
}

function Row({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): ReactElement {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-[var(--color-line-soft)]/70 pb-2 last:border-0 last:pb-0">
      <dt className="text-xs text-[var(--color-mute)]">{label}</dt>
      <dd className={cn('text-right text-sm text-[var(--color-ink)]', mono && 'font-mono text-xs')}>
        {value}
      </dd>
    </div>
  );
}

// -----------------------------------------------------------------
// Raw tab
// -----------------------------------------------------------------

function Raw({ audit }: { audit: AuditResponse }): ReactElement {
  return (
    <div className="space-y-3">
      <CodeSnippet title="Audit payload" code={JSON.stringify(audit, null, 2)} />
      {audit.report_url && (
        <p className="text-xs text-[var(--color-mute)]">
          Full Lighthouse report lives in R2 — use the <strong>Report</strong> button above to fetch
          a signed URL (5 minute TTL).
        </p>
      )}
    </div>
  );
}
